import type { ToolCall, UUID } from '@aline/common';

/**
 * Both analysis cards carry the same `rows` and `columnLabels`, so either one
 * can stand for the shape. The types come from the parsed tool call and not
 * from a local copy, so a change to the zod schema shows up here as a type
 * error.
 */
type AnalysisToolCall = Extract<ToolCall, { type: 'documentBaseAnalysis' }>;

export type AnalysisRow = NonNullable<AnalysisToolCall['rows']>[number];
export type AnalysisColumnLabels = AnalysisToolCall['columnLabels'];

/** A column the table shows: the row key it reads and the header it prints. */
export interface AnalysisColumn {
  key: string;
  label: string;
}

/**
 * Where the panel's rows come from. A card written with its rows keeps them
 * inline: a count or aggregate snapshot, or a card from before results were
 * paged. A list card carries only the count, and the panel pages its rows from
 * the query the message recorded.
 */
export type AnalysisPanelSource =
  | { kind: 'inline'; rows: AnalysisRow[] }
  | { kind: 'paged'; messageId: UUID; toolCallId: string; totalCount: number };

const EMPTY_CELL = '—';

export function getAnalysisPanelSource({
  messageId,
  rows,
  toolCallId,
  totalCount,
}: {
  messageId: UUID;
  rows: AnalysisRow[] | undefined;
  toolCallId: string;
  totalCount: number | undefined;
}): AnalysisPanelSource {
  if (rows || totalCount === undefined) return { kind: 'inline', rows: rows ?? [] };
  return { kind: 'paged', messageId, toolCallId, totalCount };
}

/** How many rows the source holds, before any of them are loaded. */
export function getAnalysisSourceRowCount(source: AnalysisPanelSource): number {
  switch (source.kind) {
    case 'inline':
      return source.rows.length;
    case 'paged':
      return source.totalCount;
  }
}

/**
 * The columns to show for inline rows, in the order the query projected them.
 *
 * `documentId` is left out when the row has a `name`, because the name cell
 * links to that document and a column of UUIDs tells the reader nothing more.
 * Count and aggregate rows have neither, so they are unaffected.
 */
export function getAnalysisColumns(rows: AnalysisRow[]): string[] {
  const [firstRow] = rows;
  if (!firstRow) return [];

  const columns = Object.keys(firstRow);
  if (!columns.includes('name')) return columns;
  return columns.filter(column => column !== 'documentId');
}

export function getInlineAnalysisColumns(
  rows: AnalysisRow[],
  columnLabels: AnalysisColumnLabels
): AnalysisColumn[] {
  return getAnalysisColumns(rows).map(key => ({
    key,
    label: getAnalysisColumnLabel(key, columnLabels),
  }));
}

/** Question columns arrive as `q_<uuid>`. The label map holds their titles. */
export function getAnalysisColumnLabel(column: string, columnLabels: AnalysisColumnLabels): string {
  return columnLabels?.[column] ?? column;
}

export function formatAnalysisCellValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return EMPTY_CELL;
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return formatAnalysisListValue(value);
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export function getRowCountLabel(count: number): string {
  if (count === 0) return 'No results';
  return formatRowCount(count);
}

/** "1 row", "1,200 rows": a count that reads inside a sentence, zero included. */
export function formatRowCount(count: number): string {
  return `${count.toLocaleString('en-US')} ${count === 1 ? 'row' : 'rows'}`;
}

/** The rows as a markdown table, which pastes as a real table into a document. */
export function buildAnalysisTableMarkdown({
  columns,
  rows,
}: {
  columns: AnalysisColumn[];
  rows: AnalysisRow[];
}): string {
  const header = columns.map(column => column.label);
  const divider = columns.map(() => '---');
  const body = rows.map(row => columns.map(column => formatAnalysisCellValue(row[column.key])));

  return [header, divider, ...body]
    .map(cells => `| ${cells.map(escapeMarkdownCell).join(' | ')} |`)
    .join('\n');
}

function formatAnalysisListValue(values: unknown[]): string {
  if (values.length === 0) return EMPTY_CELL;
  return values.map(formatAnalysisCellValue).join(', ');
}

/**
 * A pipe ends a markdown cell and a line break ends the row. Backslashes are
 * escaped first, so one already in the cell cannot cancel the escape added to
 * the pipe after it.
 */
function escapeMarkdownCell(cell: string): string {
  return cell
    .replace(/\\/g, '\\\\')
    .replace(/\|/g, '\\|')
    .replace(/\s*\n\s*/g, ' ');
}
